import { useState } from "react";
import { PrimaryButton, DangerButton } from "@/components/Buttons.jsx";
import TaxRuleModal from "./TaxRuleModal.jsx";
import { deleteTaxRule } from "@/api/taxRules.js";
import { addToast } from "@/store/app/action.js";
import { useDispatch } from "react-redux";

function TaxRulesTable({ rules, loading, onRuleChange }) {
  const dispatch = useDispatch();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const handleCreateNew = () => {
    setSelectedRule(null);
    setModalOpen(true);
  };

  const handleEditRule = (rule) => {
    setSelectedRule(rule);
    setModalOpen(true);
  };

  const handleModalSave = () => {
    onRuleChange?.();
  };

  const handleDelete = async (rule) => {
    if (!window.confirm(`Are you sure you want to delete "${rule.name}"?`)) return;
    try {
      setDeleting(rule._id);
      await deleteTaxRule(rule._id);
      dispatch(addToast({ message: 'Tax rule deleted successfully', type: 'success' }));
      onRuleChange?.();
    } catch (error) {
      dispatch(addToast({
        message: error.response?.data?.message || 'Failed to delete tax rule',
        type: 'error'
      }));
    } finally {
      setDeleting(null);
    }
  };

  const getGeographicScope = (rule) => {
    if (rule.statewide) return `Statewide (${rule.state_codes?.join(', ') || 'N/A'})`;
    if (rule.zip_codes?.length > 0) return `${rule.zip_codes.length} Zip Code${rule.zip_codes.length > 1 ? 's' : ''}`;
    if (rule.cities?.length > 0) return `${rule.cities.length} Cit${rule.cities.length > 1 ? 'ies' : 'y'}`;
    return 'None';
  };

  return (
    <div>
      {/* Create Button */}
      <div className="mb-4 flex justify-end">
        <PrimaryButton onClick={handleCreateNew}>
          Add Rule
        </PrimaryButton>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="table-auto w-full dark:text-slate-300">
          {loading ? (
            <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
              <tr>
                <td colSpan="7" className="px-2 first:pl-5 last:pr-5 py-8">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto mb-2"></div>
                    <p className="text-sm text-slate-600 dark:text-slate-400">Loading Tax Rules...</p>
                  </div>
                </td>
              </tr>
            </tbody>
          ) : (
            <>
              <thead className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-b border-slate-200 dark:border-slate-700">
                <tr>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Name</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Geographic Scope</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Rules</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Status</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left">Retailers</div>
                  </th>
                  <th className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                    <div className="font-semibold text-left"></div>
                  </th>
                </tr>
              </thead>
              <tbody className="text-sm divide-y divide-slate-200 dark:divide-slate-700">
                {rules.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-2 first:pl-5 last:pr-5 py-8 text-center text-slate-500">
                      No tax rules found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  rules.map((rule) => (
                    <tr key={rule._id}>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="font-medium text-slate-800 dark:text-slate-100">
                          {rule.name}
                        </div>
                        {rule.description && (
                          <div className="text-xs text-slate-500 mt-1">{rule.description}</div>
                        )}
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">{getGeographicScope(rule)}</div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">{rule.rules?.length || 0} rule{(rule.rules?.length || 0) !== 1 ? 's' : ''}</div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-medium rounded-full ${
                          rule.active
                            ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-400'
                            : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        }`}>
                          {rule.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="text-left">
                          {rule.sellers?.length || 0} retailer{(rule.sellers?.length || 0) !== 1 ? 's' : ''}
                        </div>
                      </td>
                      <td className="px-2 first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEditRule(rule)}
                            className="text-indigo-500 hover:text-indigo-600 dark:text-indigo-400 dark:hover:text-indigo-300"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(rule)}
                            disabled={deleting === rule._id}
                            className="text-rose-500 hover:text-rose-600 dark:text-rose-400 dark:hover:text-rose-300"
                          >
                            {deleting === rule._id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </>
          )}
        </table>
      </div>

      {/* Modal */}
      <TaxRuleModal
        isOpen={modalOpen}
        setIsOpen={setModalOpen}
        rule={selectedRule}
        onSave={handleModalSave}
      />
    </div>
  );
}

export default TaxRulesTable;
